import React from "react";
import { Box, Grid } from "@mui/material";
import { Restaurant, Landscape, Payments } from "@mui/icons-material";
import {
  ServiceCateringReport,
  ifasReportsService,
  IFASReportFilter,
} from "../../../../../services/ifasReportsService";
import { IFASReportTable, ColumnDef } from "../components/IFASReportTable";
import { ReportKpiCard, ReportStatusChip, toneFromMuiColor } from "../components/ReportPrimitives";
import { formatR } from "../../../../../utils/money";

interface Props {
  services: ServiceCateringReport[];
  loading: boolean;
  filter: IFASReportFilter;
}

export const ServicesCateringReportsTab: React.FC<Props> = ({ services, loading, filter }) => {
  const cateringCount = services.filter((s) => s.category?.toLowerCase() === "catering").length;
  const tombstoneCount = services.filter((s) => s.category?.toLowerCase() === "tombstone").length;
  const totalCostVal = services.reduce((acc, curr) => acc + (typeof curr.totalCost === "number" ? curr.totalCost : parseFloat(String(curr.totalCost || 0))), 0);

  const columns: ColumnDef<ServiceCateringReport>[] = [
    { id: "funeralNumber", label: "Funeral No", minWidth: 130 },
    { id: "category", label: "Category", minWidth: 140 },
    { id: "deceasedName", label: "Deceased Name", minWidth: 160 },
    { id: "funeralDate", label: "Funeral Date", minWidth: 130 },
    {
      id: "totalCost",
      label: "Total Cost",
      minWidth: 140,
      align: "right",
      format: (val) => formatR(val as number | string | null | undefined),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 120,
      format: (val) => (
        <ReportStatusChip
          label={val}
          tone={toneFromMuiColor(val === "CONFIRMED" || val === "COMPLETED" ? "success" : "warning")}
        />
      ),
    },
  ];

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard
            label="Catering Bookings"
            value={cateringCount}
            caption="Confirmed & completed"
            tone="ink"
            icon={<Restaurant />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard
            label="Tombstone Orders"
            value={tombstoneCount}
            caption="In production"
            tone="amber"
            icon={<Landscape />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ReportKpiCard
            label="Total Services Value"
            value={formatR(totalCostVal)}
            caption="Across all VAS lines"
            tone="green"
            icon={<Payments />}
          />
        </Grid>
      </Grid>

      <IFASReportTable
        title="Products, Value Added Services (VAS) & Catering Report"
        subtitle="Operational report covering Catering, Tombstones, Inkomo, Courtesy Vehicles, Live Streaming, Photography & Videography"
        columns={columns}
        data={services}
        loading={loading}
        onExport={(fmt) => ifasReportsService.downloadReportExport("special-services", fmt, filter)}
      />
    </Box>
  );
};
